import { useInfiniteQuery } from "@tanstack/react-query";
import { searchSchedulesV2 } from "hooks/api/schedule-v2";
import { AllScheduleForm } from "shared/types";
import { tzDate } from "shared/utils/dayjs";
import { ScheduleFilters } from "pages/schedule/filter/ScheduleFilterContext";

// V2 search has no server cursor — each page is one week, the page param is
// the ISO start-of-week date and prev/next are computed by ±7 days.
//
// `startWeek` (the first page) is part of the key: Back from Detail Jadwal
// asks for the same start week the list was opened with, so it gets the same
// cached pages, every week the admin had scrolled through, at once.
export function useGetInfiniteSchedules(
  startWeek: string,
  filters?: ScheduleFilters
) {
  return useInfiniteQuery({
    queryKey: ["schedules", "list", filters, startWeek],
    initialPageParam: startWeek,
    queryFn: async ({ pageParam }) => {
      const form: AllScheduleForm = {
        start_date: pageParam,
        // Explicit end: the server's default window is startDate.endOf("week")
        // with SUNDAY-anchored weeks, so a Monday page start would end on
        // Saturday and every week page would silently drop its Sunday.
        end_date: tzDate(pageParam).add(6, "day").toISOString(),
        type: filters?.classTypes,
        instructor_ids: filters?.instructorIds,
        customer_ids: filters?.customerIds,
        class_ids: filters?.classIds,
        // Deleted schedules are never surfaced in the UI anymore.
        include_cancelled: false,
      };
      const schedules = await searchSchedulesV2(form);
      return { pageParam, schedules };
    },
    // Kept long enough to cover a visit to a schedule's detail page.
    gcTime: 30 * 60_000,
    getNextPageParam: (_lastPage, _allPages, lastPageParam) =>
      tzDate(lastPageParam).add(7, "day").startOf("week").toISOString(),
    getPreviousPageParam: (_firstPage, _allPages, firstPageParam) =>
      tzDate(firstPageParam).subtract(7, "day").startOf("week").toISOString(),
  });
}
